import AddressSearchStatus from "./AddressSearchStatus";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Spinner } from "react-bootstrap";
import Select from "./ui/AppReactSelect";
import { toast } from "react-toastify";
import usePlacesAutocomplete, { getDetails, getGeocode, getLatLng } from "use-places-autocomplete";
import {
  Combobox,
  ComboboxInput,
  ComboboxList,
  ComboboxOption,
  ComboboxPopover,
} from "@reach/combobox";
import "@reach/combobox/styles.css";

import { placeInHouseOrderApi, apiFetchBrandSections, apiFetchBrandsByOwner, apiFetchRetailerWholesalerSections, apiCustomerLookup } from "../api/index.js";
import { apiFetchSections as apiFetchRestaurantSections } from "../api/index.js";
import { getUserFromLocalStorage } from "utilities/user";
import { useGoogleMaps } from "../GoogleMapsContext";
import { useBranchScope } from "../context/BranchScopeContext";
import { ALL, MAIN, branchAwareMessage } from "../utilities/vendorScope";

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const PAYMENT_OPTIONS = [
  { value: "COD",    label: "Cash on Delivery" },
  { value: "MOMO",   label: "MTN Mobile Money" },
  { value: "Airtel", label: "Airtel Money" },
  { value: "Visa",   label: "Visa / Card" },
  { value: "CARD",   label: "Other Card" },
];

const STEPS = ["Customer", "Menu", "Delivery", "Review & Pay"];

// ─────────────────────────────────────────────────────────────────────────────
// Tiny helpers
// ─────────────────────────────────────────────────────────────────────────────

const FieldError = ({ msg }) =>
  msg ? <div style={{ color: "#ef4444", fontSize: 11, marginTop: 3 }}>{msg}</div> : null;

const StepDot = ({ idx, current, label }) => {
  const done    = idx < current;
  const active  = idx === current;
  const color   = done ? "#16a34a" : active ? "#f2c811" : "#4b5563";
  const border  = done ? "#16a34a" : active ? "#f2c811" : "#374151";
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, flex: 1 }}>
      <div style={{
        width: 28, height: 28, borderRadius: "50%",
        background: active || done ? color : "transparent",
        border: `2px solid ${border}`,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 12, fontWeight: 700, color: active || done ? "#0f172a" : "#6b7280",
        transition: "all 0.25s",
      }}>
        {done ? "✓" : idx + 1}
      </div>
      <span style={{ fontSize: 10, color: active ? "#f2c811" : done ? "#16a34a" : "#6b7280", fontWeight: active ? 700 : 400 }}>
        {label}
      </span>
    </div>
  );
};

const StepConnector = ({ done }) => (
  <div style={{
    flex: 1, height: 2, background: done ? "#16a34a" : "#374151",
    margin: "14px 0 0", transition: "background 0.3s",
  }} />
);

// ─────────────────────────────────────────────────────────────────────────────
// Cart item row
// ─────────────────────────────────────────────────────────────────────────────

const CartRow = ({ item, onQtyChange, onRemove }) => (
  <div style={{
    display: "flex", alignItems: "center", gap: 10,
    padding: "8px 10px", background: "#1e293b", borderRadius: 8, marginBottom: 6,
  }}>
    {item.imageUrl && (
      <img src={item.imageUrl} alt={item.name}
        style={{ width: 36, height: 36, borderRadius: 6, objectFit: "cover", flexShrink: 0 }} />
    )}
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: "#f1f5f9", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
        {item.name}
      </div>
      <div style={{ fontSize: 11, color: "#94a3b8" }}>Shs {(item.price * item.qty).toLocaleString()}</div>
    </div>
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <button onClick={() => onQtyChange(item._id, item.qty - 1)}
        style={S.qtyBtn} disabled={item.qty <= 1}>−</button>
      <span style={{ color: "#f1f5f9", fontSize: 13, minWidth: 18, textAlign: "center" }}>{item.qty}</span>
      <button onClick={() => onQtyChange(item._id, item.qty + 1)} style={S.qtyBtn}>+</button>
    </div>
    <button onClick={() => onRemove(item._id)}
      style={{ background: "none", border: "none", color: "#ef4444", cursor: "pointer", fontSize: 16, padding: "0 4px" }}>
      ×
    </button>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Inline styles object
// ─────────────────────────────────────────────────────────────────────────────

const S = {
  overlay: {
    position: "fixed", inset: 0, zIndex: 9999,
    background: "rgba(0,0,0,0.45)", backdropFilter: "blur(3px)",
    display: "flex", alignItems: "center", justifyContent: "flex-end",
  },
  panel: {
    width: "100%", maxWidth: 540,
    height: "100vh", overflowY: "auto",
    background: "#ffffff",
    display: "flex", flexDirection: "column",
    boxShadow: "-8px 0 32px rgba(0,0,0,0.18)",
    animation: "slideInRight 0.3s ease-out",
  },
  header: {
    padding: "20px 24px 16px",
    borderBottom: "1px solid #e2e8f0",
    display: "flex", alignItems: "center", justifyContent: "space-between",
    flexShrink: 0,
    background: "#ffffff",
  },
  body: { flex: 1, padding: "20px 24px", overflowY: "auto", background: "#f8fafc" },
  footer: {
    padding: "16px 24px",
    borderTop: "1px solid #e2e8f0",
    display: "flex", gap: 10, flexShrink: 0,
    background: "#ffffff",
  },
  label: { fontSize: 12, fontWeight: 600, color: "#475569", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 },
  input: {
    width: "100%", padding: "10px 12px", borderRadius: 8, fontSize: 14,
    background: "#ffffff", border: "1px solid #cbd5e1", color: "#0f172a",
    outline: "none", boxSizing: "border-box",
  },
  inputError: {
    width: "100%", padding: "10px 12px", borderRadius: 8, fontSize: 14,
    background: "#ffffff", border: "1px solid #ef4444", color: "#0f172a",
    outline: "none", boxSizing: "border-box",
  },
  sectionTitle: { fontSize: 13, fontWeight: 700, color: "#64748b", margin: "18px 0 10px", textTransform: "uppercase", letterSpacing: "0.06em" },
  productCard: {
    display: "flex", alignItems: "center", gap: 10,
    padding: "10px 12px", background: "#ffffff",
    borderRadius: 8, marginBottom: 6, cursor: "pointer",
    border: "1px solid #e2e8f0", transition: "border-color 0.2s",
  },
  btn: (variant) => ({
    flex: variant === "primary" ? 1 : undefined,
    padding: "10px 20px", borderRadius: 8, border: "none",
    fontWeight: 700, fontSize: 14, cursor: "pointer",
    background: variant === "primary" ? "#f2c811"
      : variant === "danger" ? "#ef4444"
      : variant === "ghost"  ? "transparent"
      : "#f1f5f9",
    color: variant === "primary" ? "#0f172a"
      : variant === "ghost"  ? "#64748b"
      : "#0f172a",
    border: variant === "ghost" ? "1px solid #cbd5e1" : "none",
    opacity: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
  }),
  qtyBtn: {
    width: 26, height: 26, borderRadius: 6,
    background: "#e2e8f0", border: "none",
    color: "#0f172a", cursor: "pointer", fontSize: 16,
    display: "flex", alignItems: "center", justifyContent: "center",
  },
  pill: (active) => ({
    padding: "5px 12px", borderRadius: 20, fontSize: 12, fontWeight: 600,
    cursor: "pointer", border: `1px solid ${active ? "#f2c811" : "#cbd5e1"}`,
    background: active ? "#fef9c3" : "transparent",
    color: active ? "#92400e" : "#64748b",
    transition: "all 0.2s",
  }),
  summaryRow: {
    display: "flex", justifyContent: "space-between",
    padding: "8px 0", borderBottom: "1px solid #e2e8f0", fontSize: 13, color: "#334155",
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Address sub-component (reuses Google Places Autocomplete)
// ─────────────────────────────────────────────────────────────────────────────

const AddressAutocomplete = ({ value: extValue, onChange, error }) => {
  const { mapsApiLoaded } = useGoogleMaps();

  const {
    value, ready, suggestions: { data, loading: placesLoading, status: suggestionStatus },
    setValue, clearSuggestions,
  } = usePlacesAutocomplete({
    requestOptions: {
      componentRestrictions: { country: "ug" },
      location: { lat: () => 0.347596, lng: () => 32.58252 },
      radius: 200 * 1000,
      origin: { lat: 0.3148136, lng: 32.5788721 },
      strictBounds: true,
      types: ["establishment"],
    },
    debounce: 150,
  });


  const handleSelect = async (val) => {
    setValue(val, false);
    clearSuggestions();
    try {
      const results = await getGeocode({ address: val });
      const { lat, lng } = await getLatLng(results[0]);
      const details = await getDetails({ placeId: results[0].place_id });
      onChange({
        locationName: { name: details.name, vicinity: details.vicinity },
        latLng: { lat, lng },
        locationId: results[0].place_id,
        address: val,
      });
    } catch (err) {
      console.error("Geocode error", err);
    }
  };

  if (!mapsApiLoaded) return <div style={{ color: "#94a3b8", fontSize: 12 }}>Loading Google Maps...</div>;

  return (
    <Combobox onSelect={handleSelect}>
      <div style={{ position: "relative" }}>
        <ComboboxInput
          value={value}
          onChange={(e) => { setValue(e.target.value); }}
          disabled={!ready}
          placeholder="Search delivery address in Uganda..."
          style={{ ...(error ? S.inputError : S.input) }}
        />
        {value && (
          <button type="button" onClick={() => { setValue(""); clearSuggestions(); onChange(null); }}
            style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "#94a3b8", cursor: "pointer", fontSize: 16 }}>
            ×
          </button>
        )}
      </div>
      <AddressSearchStatus loading={placesLoading} ready={ready} status={suggestionStatus} count={data.length} />
      <ComboboxPopover style={{ background: "#ffffff", border: "1px solid #cbd5e1", borderRadius: 8, zIndex: 10001, fontSize: 13, boxShadow: "0 8px 24px rgba(0,0,0,0.1)" }}>
        <ComboboxList style={{ padding: 4 }}>
          {data.map(({ place_id, structured_formatting: { main_text, secondary_text } }) => (
            <ComboboxOption
              key={place_id}
              value={`${main_text} ${secondary_text}`}
              style={{ padding: "8px 10px", cursor: "pointer", color: "#0f172a", borderRadius: 6 }}
            />
          ))}
        </ComboboxList>
      </ComboboxPopover>
    </Combobox>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────

const InHouseOrderModal = ({
  open,
  onClose,
  vendorId,
  vendorType,         // "RESTAURANT" | "RETAILER" | "WHOLESALER" | "HEALTHCARE" | "ONLINE"
  vendorName,
  // SuperAdmin use-case: allow selecting any vendor
  isSuperAdmin,
  allVendors,         // [{ _id, name, operationMode }] — only for superAdmin (from getAllVendorsForSuperAdmin)
}) => {
  const user = getUserFromLocalStorage();

  /* The order is placed at the branch picked in the navbar (4 Oct 2026), so
     it reaches that branch's book, kitchen and riders. A member limited to
     several branches picks one first; staff placing for another vendor are
     not pinned (the navbar names the signed-in vendor's branches only). */
  const { active: activeBranch, activeLocation, isBranchScoped } = useBranchScope();
  const pinnedLocation = !isSuperAdmin && activeBranch !== ALL ? activeBranch : null;
  const needsBranch = !isSuperAdmin && isBranchScoped && activeBranch === ALL;
  // Said before any typing, not after the whole order is filled in.
  const PICK_BRANCH_FIRST = "Pick your branch in the top bar first. The order is placed at that branch.";

  // ── Step state ──────────────────────────────────────────────────────────────
  const [step, setStep]         = useState(0);

  // ── Step 0 — Customer info ───────────────────────────────────────────────────
  const [customerName,  setCustomerName]  = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [custErrors,    setCustErrors]    = useState({});

  // ── Phone lookup ─────────────────────────────────────────────────────────────
  const [lookupResults,  setLookupResults]  = useState([]);   // [{id, label, phone, subLabel}]
  const [lookupLoading,  setLookupLoading]  = useState(false);
  const [lookupOpen,     setLookupOpen]     = useState(false);
  const lookupTimerRef = useRef(null);

  // ── Step 1 — Menu / cart ────────────────────────────────────────────────────
  const [sections,    setSections]    = useState([]);
  const [loadingMenu, setLoadingMenu] = useState(false);
  const [cart,        setCart]        = useState([]);
  const [menuSearch,  setMenuSearch]  = useState("");

  // SuperAdmin vendor selector
  const [selectedVendor, setSelectedVendor] = useState(
    isSuperAdmin ? null : { _id: vendorId, operationMode: vendorType, name: vendorName }
  );

  const resolvedVendorId   = selectedVendor?._id   || vendorId;
  const resolvedVendorType = selectedVendor?.operationMode || vendorType;
  const resolvedVendorName = selectedVendor?.name || selectedVendor?.businessName || vendorName;

  // ── Brand ID resolution (for BRAND_OWNER vendors) ─────────────────────────
  const [resolvedBrandId, setResolvedBrandId] = useState(null);
  useEffect(() => {
    if (resolvedVendorType !== "BRAND_OWNER" || !resolvedVendorId) return;
    apiFetchBrandsByOwner(resolvedVendorId)
      .then((res) => {
        const list = res.data?.data || [];
        if (list.length > 0) setResolvedBrandId(String(list[0]._id));
      })
      .catch(() => {});
  }, [resolvedVendorId, resolvedVendorType]);

  // ── Step 2 — Delivery address ────────────────────────────────────────────────
  const [deliveryAddress, setDeliveryAddress] = useState(null);
  const [deliveryNotes,   setDeliveryNotes]   = useState("");
  const [addrError,       setAddrError]       = useState("");

  // ── Step 3 — Payment ─────────────────────────────────────────────────────────
  const [paymentMethod, setPaymentMethod] = useState("COD");
  const [momoPhone,     setMomoPhone]     = useState("");

  // ── Submission ───────────────────────────────────────────────────────────────
  const [submitting, setSubmitting] = useState(false);

  // ── Fetch menu whenever vendor resolves or changes ──────────────────────────
  // Track previous vendorId so SuperAdmin switching vendors after visiting Step 1
  // forces a fresh menu fetch even when step doesn't change.
  const prevVendorIdRef = React.useRef(null);
  useEffect(() => {
    if (!resolvedVendorId || step !== 1) return;
    // For brand vendors wait until brandId is resolved before fetching
    if (resolvedVendorType === "BRAND_OWNER" && !resolvedBrandId) return;
    // The branch is part of the key: another branch has its own sold out
    // and prices (4 Oct 2026).
    const menuKey = `${resolvedVendorId}|${pinnedLocation || ""}`;
    if (prevVendorIdRef.current !== menuKey) {
      prevVendorIdRef.current = menuKey;
      fetchMenu();
    }
  }, [resolvedVendorId, resolvedBrandId, step, pinnedLocation]);

  const fetchMenu = async () => {
    setLoadingMenu(true);
    setSections([]);
    try {
      let res;
      // Read at the pinned branch, so each line carries the price the order
      // is charged there and a product it cannot supply is marked; the
      // server would refuse those lines otherwise (409 BRANCH_LINES_CHANGED).
      const atBranch = pinnedLocation ? { outletId: pinnedLocation } : {};
      if (resolvedVendorType === "RESTAURANT") {
        res = await apiFetchRestaurantSections(resolvedVendorId, atBranch);
        setSections(res.data || []);
      } else if (resolvedVendorType === "BRAND_OWNER") {
        const brandId = resolvedBrandId || resolvedVendorId;
        res = await apiFetchBrandSections(brandId, atBranch);
        setSections(res.data?.data || []);
      } else {
        res = await apiFetchRetailerWholesalerSections(resolvedVendorId);
        setSections(res.data || []);
      }
    } catch (err) {
      console.error("Menu fetch error:", err);
      toast.error("Could not load menu. Check vendor ID.");
    } finally {
      setLoadingMenu(false);
    }
  };

  // ── Cart helpers ─────────────────────────────────────────────────────────────
  /* The catalogue's constraints travel WITH the line. Before this, a product
     entered the cart as {id, name, price, qty: 1} — every rule it carried was
     dropped at the door, which is how staff placing an order on someone's
     behalf could order 1 unit of a 50-unit-minimum product and pay COD for
     something marked prepay-only. The server rejects both now; this stops the
     modal walking into that rejection at the last step. */
  const addToCart = useCallback((product) => {
    // Sold out or switched off at the pinned branch (the read above marks it).
    if (product?.branch && product.branch.orderable === false) {
      toast.error(`${product.name || product.title} cannot be ordered at ${activeLocation?.name || "this branch"} right now.`);
      return;
    }
    const minQty = Number(product.minOrderQty) || 1;
    setCart((prev) => {
      const existing = prev.find((i) => i._id === product._id);
      if (existing) return prev.map((i) => i._id === product._id ? { ...i, qty: i.qty + 1 } : i);
      return [...prev, {
        _id:      product._id,
        name:     product.name || product.title,
        price:    product.price,
        // getSections returns 'image'; RW sections may return 'imageUrl' or 'product_image'
        imageUrl: product.imageUrl || product.image || product.product_image || null,
        vendorId: resolvedVendorId,
        // Start AT the minimum rather than at 1 — anything less is not an order
        // the vendor accepts.
        qty:      minQty,
        minOrderQty: minQty,
        requiredPaymentMethod: product.requiredPaymentMethod || "NONE",
        countInStock: product.countInStock,
      }];
    });
    toast.success(`${product.name || product.title} added`, { autoClose: 800 });
  }, [resolvedVendorId, activeLocation]);

  const changeQty = (id, qty) => {
    setCart((prev) =>
      prev.map((i) => {
        if (i._id !== id) return i;
        const floor = Number(i.minOrderQty) || 1;
        // Clamp rather than ignore: a staff member typing over the field should
        // land on the minimum, not silently keep the old value.
        return { ...i, qty: Math.max(floor, Number(qty) || floor) };
      })
    );
  };

  const removeFromCart = (id) => setCart((prev) => prev.filter((i) => i._id !== id));

  const itemsTotal = cart.reduce((s, i) => s + i.price * i.qty, 0);

  /* A product that must be prepaid cannot be sold COD, including by staff.
     Two items demanding different methods is an unsatisfiable cart. */
  const enforcedPayment = useMemo(() => {
    const required = [
      ...new Set(
        cart.map((i) => i.requiredPaymentMethod).filter((m) => m && m !== "NONE")
      ),
    ];
    return { methods: required, conflict: required.length > 1 };
  }, [cart]);

  const paymentOptions = useMemo(() => {
    if (enforcedPayment.conflict || !enforcedPayment.methods.length) {
      return PAYMENT_OPTIONS;
    }
    // Case-insensitive: the server enum is "AIRTEL", this list says "Airtel".
    const want = enforcedPayment.methods.map((m) => String(m).toUpperCase());
    const filtered = PAYMENT_OPTIONS.filter((o) =>
      want.includes(String(o.value).toUpperCase())
    );
    return filtered.length ? filtered : PAYMENT_OPTIONS;
  }, [enforcedPayment]);

  /* Keep the selection inside the allowed set — otherwise the default of "COD"
     survives a cart that forbids it. */
  useEffect(() => {
    if (!paymentOptions.some((o) => o.value === paymentMethod)) {
      setPaymentMethod(paymentOptions[0]?.value || "COD");
    }
  }, [paymentOptions, paymentMethod]);

  /* Everything standing between this form and an order. Drives both the
     disabled state and the reason line above the button. */
  const blockers = useMemo(() => {
    const list = [];
    if (enforcedPayment.conflict) {
      list.push("These items require different payment methods — place them as separate orders.");
    }
    cart.forEach((i) => {
      const floor = Number(i.minOrderQty) || 1;
      if (Number(i.qty) < floor) {
        list.push(`${i.name} has a minimum order of ${floor}.`);
      }
      if (Number(i.countInStock) === 0) list.push(`${i.name} is out of stock.`);
    });
    if (!cart.length) list.push("Add at least one item.");
    if (!customerPhone.trim()) list.push("Add the customer's phone number.");
    if (!deliveryAddress) list.push("Add a delivery address.");
    if (
      ["MOMO", "Airtel"].includes(paymentMethod) &&
      String(momoPhone || "").replace(/\D/g, "").length < 9
    ) {
      list.push("Add the mobile money number to charge.");
    }
    return list;
  }, [cart, enforcedPayment, customerPhone, deliveryAddress, paymentMethod, momoPhone]);

  // ── Validation per step ──────────────────────────────────────────────────────
  const validateStep = () => {
    if (step === 0) {
      if (needsBranch) {
        toast.error(PICK_BRANCH_FIRST);
        return false;
      }
      const errs = {};
      if (isSuperAdmin && !selectedVendor) {
        toast.error("Please select a vendor before continuing");
        return false;
      }
      if (!customerPhone.trim()) errs.customerPhone = "Phone number is required";
      else if (!/^[0-9\s\+\-]{7,15}$/.test(customerPhone.trim())) errs.customerPhone = "Enter a valid phone number";
      setCustErrors(errs);
      return Object.keys(errs).length === 0;
    }
    if (step === 1) {
      if (cart.length === 0) { toast.error("Add at least one item to the cart"); return false; }
      return true;
    }
    if (step === 2) {
      if (!deliveryAddress) { setAddrError("Delivery address is required"); return false; }
      setAddrError("");
      return true;
    }
    /* Step 3 had NO branch — it fell through to `return true`, and step 3 is
       the only step from which submit is reachable. So `if (!validateStep())`
       in handleSubmit never actually validated anything, and a required
       momoPhone went to the server as undefined. */
    if (step === 3) {
      if (blockers.length) {
        toast.error(blockers[0]);
        return false;
      }
      return true;
    }
    return true;
  };

  // ── Phone input handler — debounced customer lookup ──────────────────────────
  /* The lookup is PHONE ONLY and wants at least seven digits — matching
     `looksLikePhone` on the server, which is now enforced rather than implied.
     Two things follow. The old three-character floor fired a request on "070"
     that could only ever come back with arbitrary matches, so it is gone. And
     the value is normalised to digits (plus an optional leading +) before it is
     sent: the server strips whitespace but not dashes, so "070-077-1301" typed
     by an operator would otherwise be rejected as not phone-shaped. What the
     operator sees in the field is untouched. */
  const phoneQueryOf = (val) => {
    const cleaned = String(val || "").replace(/[^\d+]/g, "");
    return /^\+?\d{7,15}$/.test(cleaned) ? cleaned : "";
  };

  const handlePhoneChange = (val) => {
    setCustomerPhone(val);
    setCustErrors((p) => ({ ...p, customerPhone: "" }));
    clearTimeout(lookupTimerRef.current);
    const query = phoneQueryOf(val);
    if (!query) {
      setLookupResults([]);
      setLookupOpen(false);
      setLookupLoading(false);
      return;
    }
    setLookupLoading(true);
    lookupTimerRef.current = setTimeout(async () => {
      try {
        const res = await apiCustomerLookup(query, 6);
        const results = res.data?.results || [];
        setLookupResults(results);
        setLookupOpen(results.length > 0);
      } catch (_) {
        setLookupResults([]);
        setLookupOpen(false);
      } finally {
        setLookupLoading(false);
      }
    }, 400);
  };

  const selectLookupCustomer = (customer) => {
    setCustomerPhone(customer.phone || customerPhone);
    setCustomerName(customer.label || "");
    const emailMatch = (customer.subLabel || "").match(/·\s*(.+@.+)/);
    if (emailMatch) setCustomerEmail(emailMatch[1].trim());
    setLookupOpen(false);
    setLookupResults([]);
  };

  const next = () => { if (validateStep()) setStep((s) => s + 1); };
  const back = () => setStep((s) => Math.max(0, s - 1));

  // ── Submit ───────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!validateStep()) return;
    if (needsBranch) {
      toast.error(PICK_BRANCH_FIRST);
      return;
    }
    setSubmitting(true);
    try {
      const idempotencyKey = `inhouse-${Date.now()}-${Math.random().toString(36).slice(2)}`;

      const isBrandVendor = resolvedVendorType === "BRAND_OWNER";
      const effectiveBrandId = isBrandVendor ? (resolvedBrandId || resolvedVendorId) : undefined;

      const payload = {
        customerName:  customerName.trim() || customerPhone.trim(),
        customerPhone: customerPhone.trim(),
        customerEmail: customerEmail.trim() || undefined,

        orderItems: cart.map((i) => ({
          _id:      i._id,
          name:     i.name,
          price:    i.price,
          qty:      i.qty,
          imageUrl: i.imageUrl || i.image,
          vendorId: effectiveBrandId || i.vendorId,
          // The branch, for every vertical but brand (a brand order names it
          // once, below). No outletId means the main location.
          ...(!isBrandVendor && pinnedLocation && pinnedLocation !== MAIN ? { outletId: pinnedLocation } : {}),
        })),

        paymentMethod,
        momoPhone: (paymentMethod === "MOMO" || paymentMethod === "Airtel") ? momoPhone.trim() || undefined : undefined,

        shippingAddress: {
          locationName: deliveryAddress?.locationName,
          latLng:       deliveryAddress?.latLng,
          locationId:   deliveryAddress?.locationId,
          address:      deliveryAddress?.address,
        },

        itemsPrice:  itemsTotal,
        totalPrice:  itemsTotal,

        vendorId:     effectiveBrandId || resolvedVendorId,
        vendorType:   resolvedVendorType,
        isBrandOrder: isBrandVendor || undefined,
        brandId:      effectiveBrandId || undefined,
        ...(isBrandVendor && pinnedLocation && pinnedLocation !== MAIN ? { fulfilmentOutletId: pinnedLocation } : {}),

        notes: deliveryNotes.trim() || undefined,
        idempotencyKey,
      };

      const { data } = await placeInHouseOrderApi(payload);

      toast.success(`Order #${data?.order?._id?.toString().slice(-6).toUpperCase()} placed successfully!`);
      handleClose(true);
    } catch (err) {
      console.error("[InHouse] submit error:", err);
      toast.error(branchAwareMessage(err, "Failed to place order. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  // ── Reset & close ────────────────────────────────────────────────────────────
  const handleClose = (success = false) => {
    setStep(0);
    setCustomerName(""); setCustomerPhone(""); setCustomerEmail(""); setCustErrors({});
    setLookupResults([]); setLookupOpen(false); setLookupLoading(false);
    clearTimeout(lookupTimerRef.current);
    setCart([]); setSections([]); setMenuSearch("");
    setDeliveryAddress(null); setDeliveryNotes(""); setAddrError("");
    setPaymentMethod("COD"); setMomoPhone("");
    if (isSuperAdmin) setSelectedVendor(null);
    onClose(success);
  };

  if (!open) return null;

  // ── Filtered menu products ───────────────────────────────────────────────────
  const filtered = sections.map((sec) => ({
    ...sec,
    products: (sec.products || sec.restaurantProducts || []).filter((p) =>
      (p.name || p.title || "").toLowerCase().includes(menuSearch.toLowerCase())
    ),
  })).filter((sec) => sec.products.length > 0);

  // ─────────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div style={S.overlay} onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}>

    <div style={S.panel}>
      {/* ── Header ── */}
      <div style={S.header}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: "#0f172a" }}>New In-House Order</div>
          <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
            {resolvedVendorName || "Select a vendor"}
            {pinnedLocation && activeLocation ? ` · ${activeLocation.name}` : ""}
            {" · "}
            <span style={{ color: "#f2c811" }}>Phone-in / Walk-in</span>
          </div>
        </div>
        <button onClick={() => handleClose()} style={{ background: "none", border: "none", color: "#64748b", fontSize: 20, cursor: "pointer", padding: 4 }}>×</button>
      </div>

      {/* ── Progress stepper ── */}
      <div style={{ padding: "14px 24px 10px", borderBottom: "1px solid #e2e8f0", display: "flex", alignItems: "center", background: "#ffffff" }}>
        {STEPS.map((label, idx) => (
          <React.Fragment key={label}>
            <StepDot idx={idx} current={step} label={label} />
            {idx < STEPS.length - 1 && <StepConnector done={idx < step} />}
          </React.Fragment>
        ))}
      </div>

      {/* ── Body ── */}
      <div style={S.body}>

        {/* A member who runs several branches, looking at all of them: the
            order needs one branch, chosen in the top bar (4 Oct 2026). */}
        {needsBranch && (
          <div className="lk-note lk-note--warning" style={{ marginBottom: 16 }}>
            <i className="fa fa-store" aria-hidden="true" style={{ marginTop: 2 }} />
            <span>
              You work at more than one branch. Close this window, pick the branch for this order in the
              top bar, then start the order again.
            </span>
          </div>
        )}

        {/* ══ STEP 0 — Customer Info ══ */}
        {step === 0 && (
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a", marginBottom: 18 }}>
              Customer Information
            </div>

            {/* Vendor selector — only for SuperAdmin / Supervisor */}
            {isSuperAdmin && (
              <div style={{ marginBottom: 18 }}>
                <div style={S.label}>Vendor <span style={{ color: "#ef4444" }}>*</span></div>
                <Select
                  options={(allVendors || []).map((v) => ({
                    value: v._id,
                    label: `${v.businessName || v.name} — ${v.operationMode || ""}`,
                    vendor: v,
                  }))}
                  value={selectedVendor ? {
                    value: selectedVendor._id,
                    label: `${selectedVendor.businessName || selectedVendor.name} — ${selectedVendor.operationMode || ""}`,
                  } : null}
                  onChange={(opt) => {
                    setSelectedVendor(opt ? opt.vendor : null);
                    setCart([]);
                    setSections([]);
                  }}
                  placeholder="Search and select a vendor..."
                  isClearable
                  styles={{
                    control: (b) => ({ ...b, background: "#ffffff", borderColor: "#cbd5e1", minHeight: 40, fontSize: 13 }),
                    menu:    (b) => ({ ...b, background: "#ffffff", zIndex: 9999, fontSize: 13 }),
                    option:  (b, s) => ({ ...b, background: s.isFocused ? "#f1f5f9" : "#ffffff", color: "#0f172a" }),
                    singleValue: (b) => ({ ...b, color: "#0f172a" }),
                    placeholder: (b) => ({ ...b, color: "#94a3b8" }),
                  }}
                />
                {selectedVendor && (
                  <div style={{ fontSize: 11, color: "#64748b", marginTop: 4 }}>
                    Mode: <strong>{selectedVendor.operationMode}</strong>
                  </div>
                )}
              </div>
            )}

            {/* Phone first — triggers customer lookup */}
            <div style={{ marginBottom: 14, position: "relative" }}>
              <div style={S.label}>
                Phone Number <span style={{ color: "#ef4444" }}>*</span>
              </div>
              <div style={{ position: "relative" }}>
                <input
                  className="iho-input"
                  style={custErrors.customerPhone ? S.inputError : S.input}
                  placeholder="e.g. 0701234567"
                  value={customerPhone}
                  onChange={(e) => handlePhoneChange(e.target.value)}
                  onBlur={() => setTimeout(() => setLookupOpen(false), 200)}
                  inputMode="tel"
                  autoComplete="off"
                />
                {lookupLoading && (
                  <div style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", display: "flex", alignItems: "center", gap: 5 }}>
                    <span style={{ fontSize: 10, color: "#f2c811", fontWeight: 600 }}>Searching…</span>
                    <Spinner animation="border" size="sm" style={{ width: 13, height: 13, borderWidth: 2, color: "#f2c811" }} />
                  </div>
                )}
              </div>
              <FieldError msg={custErrors.customerPhone} />
              <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4 }}>
                Type a number to search existing customers, or enter a new one.
              </div>

              {/* Lookup results dropdown */}
              {lookupOpen && lookupResults.length > 0 && (
                <div style={{
                  position: "absolute", top: "100%", left: 0, right: 0,
                  background: "#ffffff", border: "1px solid #cbd5e1",
                  borderRadius: 6, zIndex: 9999, boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
                  marginTop: 2, overflow: "hidden",
                }}>
                  <div style={{ padding: "6px 12px", fontSize: 10, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.6px", borderBottom: "1px solid #f1f5f9" }}>
                    Existing customers
                  </div>
                  {lookupResults.map((c) => (
                    <div
                      key={c.id}
                      onMouseDown={() => selectLookupCustomer(c)}
                      style={{ padding: "9px 12px", cursor: "pointer", borderBottom: "1px solid #f8fafc", transition: "background 0.12s" }}
                      onMouseEnter={(e) => e.currentTarget.style.background = "#f8fafc"}
                      onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                    >
                      <div style={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>{c.label}</div>
                      <div style={{ fontSize: 11, color: "#64748b", marginTop: 1 }}>{c.subLabel}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Name — auto-filled if customer found */}
            <div style={{ marginBottom: 14 }}>
              <div style={S.label}>
                Customer Name <span style={{ color: "#64748b" }}>(optional)</span>
              </div>
              <input
                className="iho-input"
                style={S.input}
                placeholder="e.g. John Doe"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
              />
            </div>

            {/* Email — auto-filled if customer found */}
            <div style={{ marginBottom: 14 }}>
              <div style={S.label}>Email Address <span style={{ color: "#64748b" }}>(optional)</span></div>
              <input
                className="iho-input"
                style={S.input}
                placeholder="e.g. customer@email.com"
                value={customerEmail}
                type="email"
                onChange={(e) => setCustomerEmail(e.target.value)}
              />
            </div>
          </div>
          )}

          {/* ══ STEP 1 — Menu / Cart ══ */}
          {step === 1 && (
            <div>
              <div style={{
                position: "sticky", top: -20, zIndex: 10,
                display: "flex", alignItems: "center", justifyContent: "space-between",
                background: "#f8fafc", marginBottom: 14,
                padding: "10px 0 10px", marginLeft: -24, marginRight: -24, paddingLeft: 24, paddingRight: 24,
                borderBottom: cart.length > 0 ? "1px solid #e2e8f0" : "none",
              }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>Select Items</div>
                {cart.length > 0 && (
                  <div style={{ background: "#f2c811", borderRadius: 20, padding: "4px 12px", fontSize: 12, color: "#0f172a", fontWeight: 700, boxShadow: "0 1px 6px rgba(242,200,17,0.4)" }}>
                    {cart.length} item{cart.length > 1 ? "s" : ""} · Shs {itemsTotal.toLocaleString()}
                  </div>
                )}
              </div>

              {/* Search */}
              <div style={{ position: "relative", marginBottom: 14 }}>
                <input
                  className="iho-input"
                  style={{ ...S.input, paddingLeft: 34 }}
                  placeholder="Search menu..."
                  value={menuSearch}
                  onChange={(e) => setMenuSearch(e.target.value)}
                />
                <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#64748b", fontSize: 14 }}>
                  &#9906;
                </span>
              </div>

              {loadingMenu && (
                <div style={{ textAlign: "center", padding: "40px 0", color: "#64748b" }}>
                  <Spinner animation="border" size="sm" /> Loading menu...
                </div>
              )}

              {!loadingMenu && filtered.length === 0 && (
                <div style={{ textAlign: "center", padding: "40px 0", color: "#475569", fontSize: 13 }}>
                  {sections.length === 0 ? "No menu items found for this vendor." : "No items match your search."}
                </div>
              )}

              {filtered.map((section) => (
                <div key={section._id}>
                  <div style={S.sectionTitle}>{section.name || section.title}</div>
                  {(section.products || section.restaurantProducts || []).map((product) => {
                    const inCart = cart.find((i) => i._id === product._id);
                    return (
                      <div
                        key={product._id}
                        className="iho-product-card"
                        style={{ ...S.productCard, borderColor: inCart ? "#f2c811" : "#e2e8f0" }}
                        onClick={() => addToCart(product)}
                      >
                        {(product.imageUrl || product.image || product.product_image) && (
                          <img
                            src={product.imageUrl || product.image || product.product_image}
                            alt={product.name || product.title}
                            style={{ width: 44, height: 44, borderRadius: 6, objectFit: "cover", flexShrink: 0 }}
                          />
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: "#0f172a", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {product.name || product.title}
                          </div>
                          <div style={{ fontSize: 12, color: "#94a3b8" }}>Shs {(product.price || 0).toLocaleString()}</div>
                        </div>
                        {inCart ? (
                          <div style={{ fontSize: 11, color: "#f2c811", fontWeight: 700, background: "#f2c81122", borderRadius: 20, padding: "2px 8px" }}>
                            x{inCart.qty}
                          </div>
                        ) : (
                          <div style={{ fontSize: 18, color: "#475569" }}>+</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}

              {/* Cart summary */}
              {cart.length > 0 && (
                <div style={{ marginTop: 20, borderTop: "1px solid #e2e8f0", paddingTop: 16 }}>
                  <div style={S.sectionTitle}>Cart</div>
                  {cart.map((item) => (
                    <CartRow key={item._id} item={item} onQtyChange={changeQty} onRemove={removeFromCart} />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ══ STEP 2 — Delivery Address ══ */}
          {step === 2 && (
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a", marginBottom: 18 }}>
                Delivery Address
              </div>

              <div style={{ marginBottom: 16 }}>
                <div style={S.label}>Search Address <span style={{ color: "#ef4444" }}>*</span></div>
                <AddressAutocomplete
                  value={deliveryAddress?.address}
                  onChange={(addr) => { setDeliveryAddress(addr); setAddrError(""); }}
                  error={addrError}
                />
                <FieldError msg={addrError} />
                {deliveryAddress && (
                  <div style={{ marginTop: 8, padding: "8px 10px", background: "#f1f5f9", borderRadius: 8, fontSize: 12, color: "#475569" }}>
                    <span style={{ color: "#f2c811", fontWeight: 600 }}>{deliveryAddress.locationName?.name}</span>
                    {deliveryAddress.locationName?.vicinity && ` · ${deliveryAddress.locationName.vicinity}`}
                  </div>
                )}
              </div>

              <div style={{ marginBottom: 16 }}>
                <div style={S.label}>Delivery Notes <span style={{ color: "#64748b" }}>(optional)</span></div>
                <textarea
                  className="iho-input"
                  style={{ ...S.input, minHeight: 72, resize: "vertical" }}
                  placeholder="e.g. Gate colour, landmark, floor number..."
                  value={deliveryNotes}
                  onChange={(e) => setDeliveryNotes(e.target.value)}
                />
              </div>

              <div style={{ padding: "12px 14px", background: "#f1f5f9", borderRadius: 8, fontSize: 12, color: "#64748b", lineHeight: 1.6 }}>
                Ask the customer to describe their location clearly. The delivery agent will use this to reach them.
              </div>
            </div>
          )}

          {/* ══ STEP 3 — Review & Pay ══ */}
          {step === 3 && (
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a", marginBottom: 18 }}>
                Review Order
              </div>

              {/* Customer */}
              <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "12px 14px", marginBottom: 14 }}>
                <div style={{ fontSize: 11, color: "#64748b", fontWeight: 700, textTransform: "uppercase", marginBottom: 8 }}>Customer</div>
                <div style={{ fontSize: 13, color: "#0f172a" }}>{customerName || "(No name provided)"}</div>
                <div style={{ fontSize: 13, color: "#475569" }}>{customerPhone}</div>
                {customerEmail && <div style={{ fontSize: 12, color: "#64748b" }}>{customerEmail}</div>}
              </div>

              {/* Items */}
              <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "12px 14px", marginBottom: 14 }}>
                <div style={{ fontSize: 11, color: "#64748b", fontWeight: 700, textTransform: "uppercase", marginBottom: 8 }}>Order Items</div>
                {cart.map((item) => (
                  <div key={item._id} style={S.summaryRow}>
                    <span>{item.name} × {item.qty}</span>
                    <span>Shs {(item.price * item.qty).toLocaleString()}</span>
                  </div>
                ))}
                <div style={{ display: "flex", justifyContent: "space-between", paddingTop: 10, fontSize: 14, fontWeight: 700, color: "#92400e" }}>
                  <span>Total</span>
                  <span>Shs {itemsTotal.toLocaleString()}</span>
                </div>
              </div>

              {/* Delivery */}
              <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "12px 14px", marginBottom: 14 }}>
                <div style={{ fontSize: 11, color: "#64748b", fontWeight: 700, textTransform: "uppercase", marginBottom: 8 }}>Delivery</div>
                <div style={{ fontSize: 13, color: "#0f172a" }}>{deliveryAddress?.locationName?.name || deliveryAddress?.address || "—"}</div>
                {deliveryAddress?.locationName?.vicinity && (
                  <div style={{ fontSize: 12, color: "#94a3b8" }}>{deliveryAddress.locationName.vicinity}</div>
                )}
                {deliveryNotes && <div style={{ fontSize: 12, color: "#64748b", marginTop: 4, fontStyle: "italic" }}>"{deliveryNotes}"</div>}
              </div>

              {/* Payment */}
              <div style={{ marginBottom: 4 }}>
                <div style={S.label}>Payment Method</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
                  {paymentOptions.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      style={S.pill(paymentMethod === opt.value)}
                      onClick={() => setPaymentMethod(opt.value)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>

                {(paymentMethod === "MOMO" || paymentMethod === "Airtel") && (
                  <div style={{ marginTop: 10 }}>
                    <div style={S.label}>
                      {paymentMethod === "MOMO" ? "MTN MoMo" : "Airtel Money"} Number <span style={{ color: "#ef4444" }}>*</span>
                    </div>
                    <input
                      className="iho-input"
                      style={S.input}
                      placeholder={paymentMethod === "MOMO" ? "e.g. 0771234567" : "e.g. 0751234567"}
                      value={momoPhone}
                      onChange={(e) => setMomoPhone(e.target.value)}
                      inputMode="tel"
                    />
                    <div style={{ fontSize: 11, color: "#64748b", marginTop: 4 }}>
                      {paymentMethod === "MOMO"
                        ? "Enter the MTN number to debit for this payment."
                        : "Enter the Airtel number to debit for this payment."}
                    </div>
                  </div>
                )}
                {paymentMethod === "COD" && (
                  <div style={{ padding: "10px 12px", background: "#f1f5f9", borderRadius: 8, fontSize: 12, color: "#475569", lineHeight: 1.6 }}>
                    Cash will be collected upon delivery by the rider.
                  </div>
                )}
              </div>
            </div>
          )}

        </div>

        {/* Why Place Order is off. Only on the last step — earlier steps have
            their own inline validation and this would just be noise. */}
        {step === STEPS.length - 1 && blockers.length > 0 && !submitting ? (
          <div
            style={{
              background: "#FFFBEB",
              border: "1px solid #FDE68A",
              borderRadius: 8,
              padding: "8px 12px",
              margin: "0 16px 8px",
              fontSize: 12,
              color: "#92400E",
            }}
          >
            {blockers.map((b) => (
              <div key={b}>• {b}</div>
            ))}
          </div>
        ) : null}

        {/* ── Footer navigation ── */}
        <div style={S.footer}>
          {step > 0 ? (
            <button type="button" style={S.btn("ghost")} onClick={back} disabled={submitting}>
              Back
            </button>
          ) : (
            <button type="button" style={S.btn("ghost")} onClick={() => handleClose()} disabled={submitting}>
              Cancel
            </button>
          )}

          {step < STEPS.length - 1 ? (
            <button type="button" style={{ ...S.btn("primary"), flex: 1 }} onClick={next}>
              Continue
            </button>
          ) : (
            <button
              type="button"
              style={{
                ...S.btn("primary"),
                flex: 1,
                opacity: submitting || blockers.length ? 0.6 : 1,
                cursor: submitting || blockers.length ? "not-allowed" : "pointer",
              }}
              onClick={handleSubmit}
              disabled={submitting || blockers.length > 0}
            >
              {submitting ? (
                <><Spinner animation="border" size="sm" /> Placing Order...</>
              ) : (
                "Place Order"
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default InHouseOrderModal;
